// Thanh tiến trình 5 bước (redesign UX 260713) — thay hàng 11 nút phẳng.
// Mỗi bước: trạng thái (done/warn/todo/locked) + nút hành động; có nút "chạy hết còn lại".
// Gộp ref (bước ②, mở Bank drawer) + ghép phim (bước ⑤, mở Export drawer) vào 1 màn liền mạch.
import React, { useEffect, useState } from 'react';
import { LAYOUT_FOUR_GEN_HINT } from './layoutFourGuard';
import { Button, Tag, Space, Typography, Tooltip, Dropdown, Popconfirm } from 'antd';
import { genAllSummaryForPlan, genAllTooltipOf, type GenTuningLite } from './genAllUtils';
import { VideoCameraOutlined, SettingOutlined, ThunderboltOutlined, RobotOutlined } from '@ant-design/icons';
import type { EventPlan, FilmPromptMode } from '../../types/film';
// 260923 E1-P3: `computeStatus` tách sang file thuần (test được dưới jest) — re-export bên dưới để import cũ không đổi.
import { computeStatus, isAnyBusy, referencesIncomplete } from './pipelineStatus';
import { MODE_LABEL_VI, formatBlockedOrders } from './promptModeUtils';
import { stagingSummary, stagingChipLabel, stagingChipTone } from './stagingStatusUtils'; // 260930 Epic1-P3

const { Text } = Typography;

export type StageState = 'done' | 'warn' | 'todo' | 'locked';

const STATE_PILL: Record<StageState, string> = { done: 'done', warn: 'missing', todo: 'todo', locked: 'locked' };

// 260923 E1-P3: re-export để import cũ (`EventPlanTab`) không đổi.
export { computeStatus, referencesIncomplete };
export type { StepperStatus } from './pipelineStatus';

const fmtDur = (s: number) => s > 0 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '—';

interface Props {
  data: EventPlan;
  busy: Record<string, boolean>;
  onRunPass: (key: string) => void;
  onAuthor: () => void;
  /** Gen cả tập — chạy theo cấu hình ⚙️ của từng clip, không có ô chọn riêng ở đây. */
  onGenAll: () => void;
  /** eventId → cấu hình ⚙️ người dùng đặt ở clip đó; chỉ dùng để hiện trước sắp gen bằng gì. */
  genTuningByEvent: Record<string, GenTuningLite>;
  onVerify: () => void;
  onRunRemaining: () => void;
  /** 261030 C1 — plan layout four gen video ở bước ④ của khung 4 bước: ẨN nút Generate / Run remaining. */
  layoutFour?: boolean;
  onOpenBank: () => void;
  /** Mở thẳng tab "Cảnh nào mặc bộ nào" — bước này nằm trong Bank nên không ai tự tìm ra. */
  onOpenSceneLooks: () => void;
  /** Dòng gán trang phục theo cảnh của plan (chỉ cần `status` để đếm). */
  sceneLooks: { status: string }[];
  onOpenAssembly: () => void;
  onScrollEditor: () => void;
  onOpenChat: () => void; // 260715 Epic3: mở Drawer chatbot trợ lý
  /** 260722 copilot E3-P1: báo stage đang XEM lên parent → gửi kèm mỗi lượt chat làm ngữ cảnh. */
  onActiveStageChange?: (stage: { index: number; title: string }) => void;
  // Merged header (260715): breadcrumb plan gộp vào railhead thay cho label "Production progress".
  onBack?: () => void;
  planName?: string;
  planStatus?: string;
  chapterIds?: string[];
  // 260923 Epic2-P1 Bước 8 — mode prompt cấp plan + mở modal đổi mode (crumb).
  planPromptMode?: FilmPromptMode;
  onOpenChangePromptMode?: () => void;
  /** 260923 Epic2-P3 (Bước 3) — nút "Dựng lại prompt lệch mode (N)" cạnh Author prompt; N = `s.nRebuildable`. */
  onRebuildMismatch?: () => void;
  /** 260927 mục N5 — nút "Dựng lại prompt lệch bộ đồ (N)"; N = `s.nLookMismatch`. */
  onRebuildLookMismatch?: () => void;
}

const PASS_ITEMS = [
  { key: 'breakdown', label: '📍 Breakdown location' },
  { key: 'dialogue', label: '💬 Dialogue' },
  { key: 'direct', label: '🎥 Directing' },
  { key: 'durations', label: '⏱️ Duration' },
  { key: 'continuity', label: '🔗 Continuity' },
];

const PipelineStepper: React.FC<Props> = ({ data, busy, onRunPass, onAuthor, onGenAll, onVerify, onRunRemaining, onOpenBank, onOpenSceneLooks, sceneLooks, genTuningByEvent, onOpenAssembly, onScrollEditor, onOpenChat, onActiveStageChange, onBack, planName, planStatus, planPromptMode, onOpenChangePromptMode, onRebuildMismatch, onRebuildLookMismatch, layoutFour }) => {
  const s = computeStatus(data);
  // 260925 (UT6) — `isAnyBusy` loại khoá `rebuild-<eventId>` khỏi loading của "Run remaining steps" (xem
  // comment ở `pipelineStatus.ts`).
  const anyBusy = isAnyBusy(busy);

  // ① Kịch bản
  const stagingChip = stagingSummary(data.scenes.flatMap((sc) => sc.events), data.plan?.stagingVersion);
  const scriptState: StageState = s.nEvents === 0 ? 'todo' : s.scriptDone ? 'done' : 'warn';
  const scriptSub = s.nEvents === 0
    ? 'No events in plan yet'
    : <>{s.nScenes} scene · {s.nEvents} event · {s.nShots} shot · ⏱️ {fmtDur(s.totalDurationS)}{' '}
        <span style={{ marginLeft: 6 }}>
          {[['dialogue', s.hasDialogue], ['directing', s.hasShots], ['duration', s.hasDurations], ['continuity', s.hasContinuity]]
            .map(([lbl, ok]) => <span key={lbl as string} style={{ color: ok ? '#52c41a' : '#bbb', marginRight: 6 }}>{ok ? '✓' : '○'}{lbl}</span>)}
          {/* 260930 Epic1-P3 — chip "Dàn cảnh ready/total": chỉ hiện khi plan có dấu `stagingVersion` (plan cũ không có). Vàng khi còn event chưa READY. */}
          {stagingChip.show && (
            <Tooltip title="Dàn cảnh: ai có mặt ở giây đầu, ai vào sau, trạng thái vào, tình trạng cơ thể — tự chạy trong Script passes và khi dựng prompt.">
              <span style={{ color: stagingChipTone(stagingChip) === 'warn' ? '#fa8c16' : '#52c41a', marginRight: 6 }}>{stagingChipLabel(stagingChip)}</span>
            </Tooltip>
          )}
        </span></>;

  // ② Ref
  // Trang phục theo cảnh: quyết định "cảnh nào mặc bộ nào". Chưa chạy thì mọi shot rơi về bộ cũ nhất
  // (đúng bệnh "cả tập mặc một bộ"), nên nó phải HIỆN ở đây chứ không nằm im trong tab con của Bank.
  // Không có khái niệm "chờ duyệt": AI gán là có hiệu lực, người chỉ vào đổi chỗ nào không ưng.
  const slTotal = sceneLooks.length;
  const slMine = sceneLooks.filter((r: { status: string }) => r.status === 'APPROVED').length;
  const refState: StageState = !s.scriptDone ? 'locked' : (s.refsReady && slTotal > 0) ? 'done' : 'warn';
  // 260927 mục I — AVIS (mặc định) bỏ hẳn chip/chữ DC: chặn/mở Generate xét ẢNH NGƯỜI MẶC (`wornMissing`).
  // BytePlus (công tắc tay) giữ nguyên chip DC + outfits (ma-nơ-canh) như trước.
  const refSub = !s.scriptDone ? 'Finish script first' : (
    <Space size={6} wrap>
      {s.engine === 'BYTEPLUS' ? (
        <>
          <Tag color={s.dcMissing.length ? 'red' : 'green'} style={{ marginInlineEnd: 0 }}>DC {(data.refBank?.characters.length || 0) - s.dcMissing.length}/{data.refBank?.characters.length || 0}</Tag>
          <Tag color={s.plateMissing.length ? 'orange' : 'green'} style={{ marginInlineEnd: 0 }}>plates {(data.refBank?.locations.length || 0) - s.plateMissing.length}/{data.refBank?.locations.length || 0}</Tag>
          <Tag color={s.outfitMissing.length ? 'orange' : 'green'} style={{ marginInlineEnd: 0 }}>outfits {(data.refBank?.characters.length || 0) - s.outfitMissing.length}/{data.refBank?.characters.length || 0}</Tag>
          {s.dcMissing.length > 0 && <Text type="danger" style={{ fontSize: 11 }}>✗DC: {s.dcMissing.join(', ')} (assign manually in "Manage references")</Text>}
          {s.dcMissing.length === 0 && (s.outfitMissing.length > 0 || s.plateMissing.length > 0) &&
            <Text type="warning" style={{ fontSize: 11 }}>missing: {[...s.plateMissing.map((n) => 'plate ' + n), ...s.outfitMissing.map((n) => 'outfit ' + n)].join(', ')}</Text>}
        </>
      ) : (
        <>
          <Tag color={s.wornMissing.length ? 'red' : 'green'} style={{ marginInlineEnd: 0 }}>worn photos {(data.refBank?.characters.length || 0) - s.wornMissing.length}/{data.refBank?.characters.length || 0}</Tag>
          <Tag color={s.plateMissing.length ? 'orange' : 'green'} style={{ marginInlineEnd: 0 }}>plates {(data.refBank?.locations.length || 0) - s.plateMissing.length}/{data.refBank?.locations.length || 0}</Tag>
          {s.wornMissing.length > 0 && <Text type="danger" style={{ fontSize: 11 }}>✗ missing worn photo: {s.wornMissing.join(', ')} — open "Custom Prompt" on their card in the Character Bank</Text>}
          {s.wornMissing.length === 0 && s.plateMissing.length > 0 &&
            <Text type="warning" style={{ fontSize: 11 }}>missing: {s.plateMissing.map((n) => 'plate ' + n).join(', ')}</Text>}
        </>
      )}
      <Tag color={slTotal ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>
        {slTotal ? `scene outfits: ${slTotal} appearances${slMine ? ` · ${slMine} yours` : ''}` : 'scene outfits: NOT ASSIGNED'}
      </Tag>
      {!slTotal && <Text type="danger" style={{ fontSize: 11 }}>no outfit assigned to any scene → the whole episode will wear one outfit</Text>}
    </Space>
  );

  // ③ Author — 260923 Epic2-P1 (L12, rr1b #1): hiện theo `promptsReady` (KHÔNG theo `authorDone` — plan Tự
  // nhiên 0 prompt vẫn `authorDone=true` nhưng chưa gen được tới, không được hiện xanh).
  const authorState: StageState = !s.scriptDone ? 'locked' : s.promptsReady ? 'done' : ((s.nAuthored > 0 || s.nModeMismatch > 0) ? 'warn' : 'todo');
  const authorModeNote = [s.nModeMismatch > 0 ? `· ${s.nModeMismatch} lệch mode` : '', s.nMissing > 0 ? `· ${s.nMissing} chưa có prompt` : ''].filter(Boolean).join(' ');

  // ④ Gen
  const genState: StageState = !s.authorDone ? 'locked' : s.genDone ? 'done' : (s.clipsDone > 0 ? 'warn' : 'todo');

  // ⑤ Ghép
  const asmState: StageState = s.clipsDone === 0 ? 'locked' : 'todo';

  // Bước đang chọn (mặc định: bước chưa-done đầu tiên). Bấm node để xem hành động của bước đó.
  const [active, setActive] = useState(() => {
    const i = [scriptState, refState, authorState, genState, asmState].findIndex((st) => st !== 'done');
    return i < 0 ? 4 : i;
  });

  const nChars = data.refBank?.characters.length || 0;
  // 260927 mục I — AVIS đếm theo ẢNH NGƯỜI MẶC (wornMissing), BytePlus vẫn đếm DC.
  const faces = nChars - (s.engine === 'BYTEPLUS' ? s.dcMissing.length : s.wornMissing.length);

  const steps: { n: string; title: string; state: StageState; hint: string; nodeSub: string; fullSub: React.ReactNode; actions: React.ReactNode }[] = [
    {
      n: '1', title: 'Script', state: scriptState,
      hint: 'Turn a story chapter into a shooting script: split into events, write dialogue, design shots/camera angles, compute durations, check continuity.',
      nodeSub: s.nEvents === 0 ? 'no events' : `${s.nEvents} events · ${fmtDur(s.totalDurationS)}`,
      fullSub: scriptSub,
      actions: <Space size={4} wrap>
        {s.nEvents > 0 && <Tooltip title="Scroll down to the Scene → Event → Shot editor to view/edit manually"><Button size="small" onClick={onScrollEditor}>View/edit</Button></Tooltip>}
        <Tooltip title="Re-run each script pass (dialogue / breakdown / directing / duration / continuity)">
          <Dropdown menu={{ items: PASS_ITEMS.map((p) => ({ key: p.key, label: p.label, onClick: () => onRunPass(p.key) })) }}>
            <Button size="small" loading={PASS_ITEMS.some((p) => busy[p.key])}>Run pass ▾</Button>
          </Dropdown>
        </Tooltip>
        {s.scriptDone && <Tooltip title="Score plan: linter (hard errors) + AI judge (score 1-5)"><Button size="small" loading={busy.verify} onClick={onVerify}>⚑ Score</Button></Tooltip>}
      </Space>,
    },
    {
      n: '2', title: 'References', state: refState,
      hint: s.engine === 'BYTEPLUS'
        ? 'Sample images so the AI generates correctly: character faces (Digital Character), outfits, and background plates for each location.'
        : 'Sample images so the AI generates correctly: a worn photo (person wearing the outfit) for each character, and background plates for each location.',
      nodeSub: !s.scriptDone ? 'locked' : s.refsReady ? 'all ready' : `${faces}/${nChars} ${s.engine === 'BYTEPLUS' ? 'faces' : 'worn photos'}`,
      fullSub: refSub,
      actions: <Space size={4} wrap>
        {/* 260805: bỏ 2 nút "Generate plates" / "Outfits" ở đây — gen ảnh nay đi qua Bank drawer
            (khối World & Period → Regenerate), nơi có đếm trước + xác nhận phí. Hai nút này bắn thẳng
            job nền cho CẢ plan mà không nói sẽ gen bao nhiêu ảnh. */}
        <Tooltip title={s.engine === 'BYTEPLUS' ? 'Assign digital character (face) to characters + view Look & Location bank' : 'Generate worn photos for characters + view Look & Location bank'}><Button size="small" icon={<SettingOutlined />} onClick={onOpenBank} disabled={!s.scriptDone}>Manage references</Button></Tooltip>
        <Tooltip title="Review/change who wears what in each scene. Skip it and every shot falls back to the character's oldest outfit.">
          <Button size="small" type={slTotal ? 'default' : 'primary'} ghost={!slTotal}
            onClick={onOpenSceneLooks} disabled={!s.scriptDone}>
            🧥 Scene outfits
          </Button>
        </Tooltip>
      </Space>,
    },
    {
      n: '3', title: 'Prompts', state: authorState,
      hint: 'AI writes a detailed prompt sent to the Seedance engine for each event — combining shots, references, dialogue, continuity.',
      nodeSub: !s.scriptDone ? 'locked' : `${s.nAuthored}/${s.nEvents} prompts${authorModeNote ? ` ${authorModeNote}` : ''}`,
      fullSub: authorState === 'locked' ? 'Finish the script first' : `${s.nAuthored}/${s.nEvents} events have prompts${authorModeNote ? ` ${authorModeNote}` : ''}`,
      actions: <Space size={4} wrap>
        {/* 260927 (phiên test luồng): kiểu prompt của plan nằm ở bước này (trước ở header plan). */}
        {planPromptMode && (
          <Tag color={planPromptMode === 'NATURAL' ? 'purple' : 'blue'} style={{ marginInlineEnd: 0 }}>
            {MODE_LABEL_VI[planPromptMode]}
          </Tag>
        )}
        {onOpenChangePromptMode && (
          <Button size="small" type="link" style={{ paddingInline: 4 }} onClick={onOpenChangePromptMode}>Đổi kiểu prompt</Button>
        )}
        <Tooltip title="AI builds the Seedance prompt (8 layers) for each event from shots + continuity"><Button size="small" loading={busy.author} onClick={onAuthor} disabled={!s.scriptDone}>Author prompt</Button></Tooltip>
        {/* 260923 Epic2-P3 (Bước 3) — N = s.nRebuildable (CÙNG vị từ shouldRebuildMismatchOf); N=0 ⇒ ẩn nút. */}
        {s.nRebuildable > 0 && onRebuildMismatch && (
          <Tooltip title="Dựng lại prompt cho những event AI viết đang lệch mode hiện tại (không đụng prompt sửa tay — EDITED)">
            <Popconfirm title={`Dựng lại ${s.nRebuildable} prompt (${s.nRebuildable} lượt AI)?`} okText="Dựng lại" onConfirm={onRebuildMismatch}>
              <Button size="small" loading={busy.rebuildMismatch}>Dựng lại prompt lệch mode ({s.nRebuildable})</Button>
            </Popconfirm>
          </Tooltip>
        )}
        {/* 260927 mục N5 — chip look: trong prompt ≠ bảng gán cảnh hiện tại (đổi bộ / xoá dòng / AI gán lại
            SAU khi đã author). N = s.nLookMismatch (`plan.lookMismatchEventIds`, BE tính lúc đọc). */}
        {s.nLookMismatch > 0 && onRebuildLookMismatch && (
          <Tooltip title="Dựng lại prompt cho những event có chip trang phục đang trỏ bộ KHÁC bộ đang gán cho cảnh đó (không đụng prompt sửa tay — EDITED)">
            <Popconfirm title={`Dựng lại ${s.nLookMismatch} prompt lệch bộ đồ (${s.nLookMismatch} lượt AI)?`} okText="Dựng lại" onConfirm={onRebuildLookMismatch}>
              <Button size="small" loading={busy.rebuildLookMismatch}>Dựng lại prompt lệch bộ đồ ({s.nLookMismatch})</Button>
            </Popconfirm>
          </Tooltip>
        )}
      </Space>,
    },
    {
      n: '4', title: 'Generate', state: genState,
      hint: s.engine === 'BYTEPLUS'
        ? 'Generate video for each event with Seedance. Needs enough Digital Characters (faces) first, otherwise faces will be wrong.'
        : 'Generate video for each event with Seedance. Needs a worn photo for each character first, otherwise faces/outfits will be wrong.',
      nodeSub: !s.authorDone ? 'locked' : `${s.clipsDone}/${s.nEvents} clips`,
      fullSub: genState === 'locked' ? 'Author the prompts first' : <>{s.clipsDone}/{s.nEvents} clips{s.refsBlocked && <Text type="danger" style={{ fontSize: 11, marginLeft: 6 }}>⚠ missing {s.engine === 'BYTEPLUS' ? 'DC' : 'worn photo'} — assign in step 2 to enable generation</Text>}</>,
      // 260819 — gen cả tập chạy theo cấu hình ⚙️ của TỪNG clip. Không có ô chọn model thứ hai ở đây:
      // hai chỗ chọn cùng một thứ thì sớm muộn cũng lệch nhau, và người dùng không biết cái nào thắng.
      actions: layoutFour ? <Text type="secondary" style={{ fontSize: 12 }} data-testid="layout-four-gen-hint">{LAYOUT_FOUR_GEN_HINT}</Text> : (() => {
        // 260923 Epic2-P1 (Bước 8) — "còn phải gen" = event `OK` (gen được, qua cửa mode) CHƯA chọn clip.
        // Event bị chặn (MISMATCH/MISSING) không vào `pendingIds` nữa — chúng không gen được, liệt kê riêng.
        // F1 (24/09): dùng THẲNG `s.genReadyIds` — CÙNG một vị từ với `nGenReady` (không tự lọc lại ở đây
        // nữa: bộ lọc cũ còn loại thêm event `OK` đã có clip DONE nhưng chưa chọn ⇒ đếm ⚙️ override lệch).
        const pendingIds = s.genReadyIds;
        const pending = s.nGenReady;
        // Cấu hình HIỆU LỰC: video của plan opt-in dùng cấu hình ĐÃ LƯU (BE resolve từ đó, bỏ qua ⚙️ theo lượt) ⇒ hộp xác nhận + tooltip nói đúng thứ sẽ chạy.
        const g = genAllSummaryForPlan({ events: data.scenes.flatMap((scene) => scene.events), pendingIds, tuningByEvent: genTuningByEvent, flowVersion: data.plan.flowVersion });
        const blockedNote = s.blockedOrders.length > 0
          ? `${s.blockedOrders.length} event bị chặn (lệch mode/chưa có prompt), sẽ KHÔNG gen: ${formatBlockedOrders(s.blockedOrders)}.`
          : '';
        const confirmTitle = blockedNote ? `${blockedNote}\n${g.confirm}` : g.confirm;
        const btn = (
          <Button size="small" type="primary" icon={<VideoCameraOutlined />} loading={busy.genall}
            danger={g.danger} disabled={!s.authorDone || s.refsBlocked || !pending}>
            {g.label}
          </Button>
        );
        const tooltipTitle = s.refsBlocked
          ? (s.engine === 'BYTEPLUS'
            ? 'Locked: assign all Digital Characters (faces) in step 2 to enable generation'
            : 'Locked: generate a worn photo for every character in step 2 to enable generation')
          : (s.authorDone && !pending)
            ? `Nothing to generate — ${s.blockedOrders.length ? `${s.blockedOrders.length} event bị chặn cửa mode` : 'every event already has a clip'}.`
            : genAllTooltipOf(g.hasSaved);
        return (
          <Tooltip title={tooltipTitle}>
            <span>
              {s.authorDone && !s.refsBlocked && pending > 0
                ? <Popconfirm title={confirmTitle} okText="Generate" okButtonProps={{ danger: g.danger }} onConfirm={onGenAll}>{btn}</Popconfirm>
                : btn}
            </span>
          </Tooltip>
        );
      })(),
    },
    {
      n: '5', title: 'Assemble', state: asmState,
      hint: 'Stitch the selected clips of every event into a single vertical 9:16 MP4 film, with subtitles synced to the dialogue.',
      nodeSub: data.plan.exportStale ? 'out of date' : (s.clipsDone === 0 ? 'locked' : `${s.clipsDone}/${s.nEvents} ready`),
      fullSub: data.plan.exportStale
        ? <Text type="danger" style={{ fontSize: 12 }}>⚠ Film is out of date after edits — re-assemble to refresh the MP4.</Text>
        : (asmState === 'locked' ? 'Need at least 1 selected clip' : `${s.clipsDone}/${s.nEvents} clips ready to assemble`),
      actions: <Tooltip title="Assemble the selected clips → 1 vertical 9:16 MP4 file (audio + timed subtitles)"><Button size="small" type={data.plan.exportStale ? 'primary' : 'default'} onClick={onOpenAssembly} disabled={s.clipsDone === 0}>Open assembly</Button></Tooltip>,
    },
  ];
  const cur = steps[active] || steps[0];

  // E3-P1: báo stage đang xem lên parent (kể cả giá trị KHỞI TẠO — nên dùng effect, không nhét vào onClick).
  useEffect(() => {
    onActiveStageChange?.({ index: active, title: (steps[active] || steps[0]).title });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="sb-railhead">
        {planName ? (
          <span className="sb-crumb">
            <span className="root" onClick={onBack}>Plans</span>
            <span className="sep">›</span>
            <span className="cur">{planName}</span>
            {planStatus && <span className="sb-wtag" style={{ marginLeft: 8 }}>{planStatus}</span>}
            {/* 260723: gỡ Select "dạng phim I/II" khỏi UI (detector code vẫn tự chạy khi tạo plan). */}
            {/* 260927 (phiên test luồng): tag mode + "Đổi kiểu prompt" dời xuống bước 3 Prompts — mode chỉ có tác dụng ở đó. */}
          </span>
        ) : <span className="t">Production progress</span>}
        <Space>
          <Tooltip title="Ask the AI assistant to help write or fix dialogue, action and prompts — it proposes edits into your draft for review.">
            <Button size="small" icon={<RobotOutlined />} onClick={onOpenChat} disabled={s.nEvents === 0}>Assistant</Button>
          </Tooltip>
          {!layoutFour && <Tooltip title="Automatically runs the unfinished steps in sequence: script (wait) → plates/outfits/prompt (background). Stops so you can assign DC / press Generate.">
            <Button type="primary" size="small" icon={<ThunderboltOutlined />} loading={anyBusy}
              disabled={s.nEvents === 0 || (s.scriptDone && s.refsReady && s.authorDone && s.genDone)}
              onClick={onRunRemaining}>Run remaining steps</Button>
          </Tooltip>}
        </Space>
      </div>
      <div className="sb-hrail">
        {steps.map((st, i) => (
          <button key={st.n} type="button" title={st.hint} className={`sb-node${i === active ? ' active' : ''}`} onClick={() => setActive(i)}>
            <span className={`sb-step-dot ${st.state}`}>{st.state === 'done' ? '✓' : st.n}</span>
            <span className="nlab"><span className="t">{st.title}</span><span className="s">{st.nodeSub}</span></span>
          </button>
        ))}
      </div>
      <div className="sb-actbar">
        <span className="lbl">{cur.title}<span className={`sb-state ${cur.state}`} style={{ marginLeft: 6 }}>{STATE_PILL[cur.state]}</span></span>
        {cur.actions}
      </div>
      {cur.fullSub && <div style={{ fontSize: 12, color: 'var(--sb-soft)', margin: '9px 2px 0' }}>{cur.fullSub}</div>}
    </div>
  );
};

export default PipelineStepper;
